import { apiClient } from './client'

export interface Institution {
  id: string
  name: string
  slug: string
  subscriptionPlan: 'basic' | 'premium' | 'enterprise'
  isActive: boolean
  status?: 'active' | 'inactive' | 'suspended'
  type?: 'primary' | 'secondary' | 'university' | 'kindergarten'
  createdAt: string
  studentCount?: number
  teacherCount?: number
  totalUsers?: number
  location?: {
    latitude: number
    longitude: number
    address?: string
  }
  settings: {
    address: string
    city: string
    state: string
    phone: string
    countryCode?: string
    email: string
    timezone: string
    currency: string
    features?: {
      rfid?: boolean
      transport?: boolean
      attendance?: boolean
      meals?: boolean
    }
    transport?: {
      enabled?: boolean
      requireStudentSelection?: boolean
      autoCreateSessions?: boolean
      defaultPickupTime?: string
      defaultDropoffTime?: string
      allowMultipleTripsPerDay?: boolean
      trackingEnabled?: boolean
      parentNotifications?: boolean
      emergencyContacts?: any[]
    }
  }
}

export interface InstitutionsResponse {
  institutions: Institution[]
  pagination: {
    currentPage: number
    totalPages: number
    totalInstitutions: number
    limit: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
  filters: {
    search: string | null
    status: string | null
    type: string | null
    sortBy: string
    sortOrder: string
  }
}

export interface GetInstitutionsParams {
  page?: number
  limit?: number
  search?: string
  status?: 'active' | 'inactive' | 'suspended'
  type?: 'primary' | 'secondary' | 'university' | 'kindergarten'
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'studentCount' | 'teacherCount'
  sortOrder?: 'asc' | 'desc'
}

export interface InstitutionStats {
  total: number
  active: number
  inactive: number
  totalUsers: number
  byPlan: {
    basic: number
    premium: number
    enterprise: number
  }
}

export interface CreateInstitutionRequest {
  institution: {
    name: string
    address: string
    city: string
    state: string
    phone: string
    countryCode?: string
    email: string
    subscriptionPlan: 'basic' | 'premium' | 'enterprise'
    timezone: string
    currency: string
    location: {
      latitude: number
      longitude: number
      address?: string
    }
  }
  admin: {
    firstName: string
    lastName: string
    email: string
    phone?: string
    countryCode?: string
    roleId?: string
  }
}

export interface UpdateInstitutionRequest {
  name?: string
  address?: string
  city?: string
  state?: string
  phone?: string
  countryCode?: string
  email?: string
  subscriptionPlan?: 'basic' | 'premium' | 'enterprise'
  timezone?: string
  currency?: string
  isActive?: boolean
  location?: {
    latitude: number
    longitude: number
    address?: string
  }
  settings?: {
    address?: string
    city?: string
    state?: string
    phone?: string
    countryCode?: string
    email?: string
    timezone?: string
    currency?: string
    features?: {
      rfid?: boolean
      transport?: boolean
      attendance?: boolean
      meals?: boolean
    }
    transport?: {
      enabled?: boolean
      requireStudentSelection?: boolean
      autoCreateSessions?: boolean
      defaultPickupTime?: string
      defaultDropoffTime?: string
      allowMultipleTripsPerDay?: boolean
      trackingEnabled?: boolean
      parentNotifications?: boolean
      emergencyContacts?: any[]
    }
  }
}

export const institutionsApi = {
  // Get all institutions with search and pagination
  getInstitutions: async (params: GetInstitutionsParams = {}): Promise<InstitutionsResponse> => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const response = await apiClient.get(`/institutions?${queryParams.toString()}`);
    return response.data.data;
  },

  // Get all institutions (simple) - for backward compatibility
  getAllInstitutions: async (): Promise<Institution[]> => {
    const response = await institutionsApi.getInstitutions({ limit: 1000 });
    return response.institutions;
  },

  // Get single institution by ID
  getInstitution: async (id: string): Promise<Institution> => {
    const response = await apiClient.get(`/institutions/${id}`)
    return response.data.data
  },

  // Create new institution with admin
  createInstitutionWithAdmin: async (data: CreateInstitutionRequest): Promise<{
    institution: Institution,
    admin: {
      id: string
      firstName: string
      lastName: string
      email: string
      role: string
      temporaryPassword: string
    }
  }> => {
    const response = await apiClient.post('/institutions', data)
    return response.data.data
  },

  // Update institution
  updateInstitution: async (id: string, data: UpdateInstitutionRequest): Promise<Institution> => {
    const response = await apiClient.put(`/institutions/${id}`, data)
    return response.data.data
  },

  // Delete institution
  deleteInstitution: async (id: string): Promise<void> => {
    await apiClient.delete(`/institutions/${id}`)
  },

  // Get institution statistics
  getInstitutionStats: async (): Promise<InstitutionStats> => {
    const response = await apiClient.get('/institutions/stats')
    return response.data.data
  }
}

// Export individual functions for direct import
export const getInstitutions = institutionsApi.getInstitutions
export const getInstitution = institutionsApi.getInstitution
export const createInstitutionWithAdmin = institutionsApi.createInstitutionWithAdmin
export const updateInstitution = institutionsApi.updateInstitution
export const deleteInstitution = institutionsApi.deleteInstitution
export const getInstitutionStats = institutionsApi.getInstitutionStats